import {getTestProps} from '@benchling/js-test-utils';
import {css} from 'aphrodite';
import React, {useCallback, useState} from 'react';

import {agentProgressStyles} from 'llm/chat-library/messages/ChatProgress';
import {ToolActivityItem} from 'llm/chat-library/messages/ToolActivityItem';
import {ProgressData, ProgressToolCall} from 'llm/chat-library/progress/ProgressTypes';
import {ToolDetail} from 'llm/chat-library/tool-details/ToolDetail';
import {shouldDisplayTool} from 'llm/chat-library/tool-details/types';
import {useToolDisplayName} from 'llm/chat-library/tool-details/useToolDisplayName';
import {useToolIcon} from 'llm/chat-library/tool-details/useToolIcon';
import {MCPToolMappingProvider} from 'llm/mcp-client-library/MCPToolMappingContext';
import {ProcedureToolMappingProvider} from 'llm/procedure-tool-library/ProcedureToolMappingContext';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import {ExpandableModal} from 'ui-library/modals/ExpandableModal';
import {ModalButton} from 'ui-library/modals/Modal';

const ACTIVITY_MODAL_KEY = 'ACTIVITY_MODAL_KEY';
const TEST_COMPONENT = 'DeepResearchProgress';

export interface ActivityModalControls {
  openActivityModal: (activityData: ProgressData, selectedToolId?: string) => void;
}

export function useActivityModalControls(): ActivityModalControls {
  const openActivityModal = useCallback((activityData: ProgressData, selectedToolId?: string) => {
    LegacyModalContainer.open(
      ACTIVITY_MODAL_KEY,
      <ActivityModal
        activityData={activityData}
        initialSelectedToolId={selectedToolId}
        closeModal={() => {
          LegacyModalContainer.close(ACTIVITY_MODAL_KEY);
        }}
      />
    );
  }, []);

  return {
    openActivityModal,
  };
}

export interface ActivityModalProps {
  activityData: ProgressData;
  closeModal: () => void;
  initialSelectedToolId?: string;
}

function ActivityList({
  activityData,
  onToolClick,
}: {
  activityData: ProgressData;
  onToolClick: (toolCall: ProgressToolCall) => void;
}): JSX.Element {
  const getToolDisplayNameFormatted = useToolDisplayName();
  const getToolIconResolved = useToolIcon();
  const visibleToolCalls = activityData.toolCalls.filter((tc) => shouldDisplayTool(tc.tool));

  return (
    <div
      className={css(agentProgressStyles.activityContent)}
      {...getTestProps({
        component: TEST_COMPONENT,
        element: 'content',
      })}
    >
      {visibleToolCalls.map((toolCall, index) => (
        <ToolActivityItem
          key={toolCall.toolId || index}
          icon={getToolIconResolved(toolCall.tool)}
          displayName={getToolDisplayNameFormatted(toolCall.tool, toolCall.args, toolCall.status)}
          status={toolCall.status}
          onClick={() => {
            onToolClick(toolCall);
          }}
          testComponent={TEST_COMPONENT}
          testKey={`${index}`}
        />
      ))}
    </div>
  );
}

/**
 * Modal for displaying detailed progress activity for completed deep research.
 * Shows the full activity thread.
 */
export function ActivityModal({
  activityData,
  closeModal,
  initialSelectedToolId,
}: ActivityModalProps): JSX.Element {
  const {toolCalls} = activityData;

  // Find initial tool if ID provided
  const initialTool = initialSelectedToolId
    ? toolCalls.find((tool) => tool.toolId === initialSelectedToolId) || null
    : null;

  const [selectedTool, setSelectedTool] = useState<ProgressToolCall | null>(initialTool);

  const handleToolClick = useCallback((toolCall: ProgressToolCall) => {
    setSelectedTool(toolCall);
  }, []);

  const handleBackToActivity = useCallback(() => {
    setSelectedTool(null);
  }, []);

  const buttons: Array<ModalButton> = selectedTool
    ? [
        {
          text: 'Back',
          onClick: handleBackToActivity,
        },
        {
          text: 'Close',
          onClick: closeModal,
        },
      ]
    : [
        {
          text: 'Close',
          onClick: closeModal,
        },
      ];

  const title = selectedTool ? 'Tool Details' : 'Activity';

  return (
    <MCPToolMappingProvider>
      <ProcedureToolMappingProvider toolCalls={toolCalls}>
        <ExpandableModal
          title={title}
          onRequestClose={closeModal}
          buttons={buttons}
          closeableOnEscapeKey={true}
        >
          {selectedTool ? (
            <ToolDetail toolCall={selectedTool} />
          ) : (
            <ActivityList activityData={activityData} onToolClick={handleToolClick} />
          )}
        </ExpandableModal>
      </ProcedureToolMappingProvider>
    </MCPToolMappingProvider>
  );
}
